import { useEffect, useRef, useState, useSyncExternalStore } from 'react'

// AB#1846 — promise-based in-app dialogs that replace the native window.confirm / window.prompt /
// window.alert. A module-level singleton means call sites just `await confirmDialog(...)` instead of
// `confirm(...)` (no per-component wiring); each app mounts <DialogHost /> once at its root.

type Req =
    | { kind: 'confirm'; message: string; title?: string; confirmLabel?: string; danger?: boolean; resolve: (v: boolean) => void }
    | { kind: 'prompt'; message: string; title?: string; defaultValue?: string; placeholder?: string; confirmLabel?: string; resolve: (v: string | null) => void }
    | { kind: 'alert'; message: string; title?: string; resolve: () => void }

let current: Req | null = null
const listeners = new Set<() => void>()
const emit = () => listeners.forEach(l => l())
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l) } }
const getSnapshot = () => current

export function confirmDialog(message: string, opts?: { title?: string; confirmLabel?: string; danger?: boolean }): Promise<boolean> {
    return new Promise(resolve => { current = { kind: 'confirm', message, ...opts, resolve }; emit() })
}

export function promptDialog(message: string, opts?: { title?: string; defaultValue?: string; placeholder?: string; confirmLabel?: string }): Promise<string | null> {
    return new Promise(resolve => { current = { kind: 'prompt', message, ...opts, resolve }; emit() })
}

export function alertDialog(message: string, opts?: { title?: string }): Promise<void> {
    return new Promise(resolve => { current = { kind: 'alert', message, ...opts, resolve }; emit() })
}

export function DialogHost() {
    const req = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
    const [value, setValue] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (req?.kind === 'prompt') {
            setValue(req.defaultValue ?? '')
            // focus after the dialog mounts
            const t = setTimeout(() => inputRef.current?.focus(), 0)
            return () => clearTimeout(t)
        }
        return undefined
    }, [req])

    if (!req) { return null }

    const close = () => { current = null; emit() }
    const onCancel = () => {
        if (req.kind === 'confirm') { req.resolve(false) }
        else if (req.kind === 'prompt') { req.resolve(null) }
        else { req.resolve() }
        close()
    }
    const onConfirm = () => {
        if (req.kind === 'confirm') { req.resolve(true) }
        else if (req.kind === 'prompt') { req.resolve(value.trim() ? value.trim() : null) }
        else { req.resolve() }
        close()
    }

    return (
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
            onMouseDown={e => { if (e.target === e.currentTarget) { onCancel() } }}
        >
            <div className="w-full max-w-md rounded-lg bg-white dark:bg-slate-800 shadow-xl border border-neutral-200 dark:border-slate-700 p-5"
                onKeyDown={e => { if (e.key === 'Escape') { onCancel() } else if (e.key === 'Enter' && req.kind !== 'prompt') { onConfirm() } }}>
                {req.title && <h2 className="text-sm font-semibold text-neutral-900 dark:text-slate-100 mb-1">{req.title}</h2>}
                <p className="text-sm text-neutral-700 dark:text-slate-300 whitespace-pre-line">{req.message}</p>
                {req.kind === 'prompt' && (
                    <input
                        ref={inputRef}
                        value={value}
                        onChange={e => setValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { onConfirm() } }}
                        placeholder={req.placeholder}
                        className="mt-3 w-full rounded border border-neutral-300 dark:border-slate-600 px-2 py-1.5 text-sm dark:bg-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                )}
                <div className="mt-4 flex justify-end gap-2">
                    {req.kind !== 'alert' && (
                        <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded-md border border-neutral-200 dark:border-slate-600 text-neutral-700 dark:text-slate-300 hover:bg-neutral-50 dark:hover:bg-slate-700">
                            Cancel
                        </button>
                    )}
                    <button
                        onClick={onConfirm}
                        className={`px-3 py-1.5 text-sm rounded-md text-white ${req.kind === 'confirm' && req.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {(req.kind === 'alert' ? 'OK' : (req.kind === 'confirm' ? req.confirmLabel : req.confirmLabel)) ?? (req.kind === 'confirm' ? 'Confirm' : 'OK')}
                    </button>
                </div>
            </div>
        </div>
    )
}
